import Game from "./scenes/Game";
import gameInfo from '../assets/json/gameInfo.json';
import ABCyaLib from "@abcya/abcya_lib";

/**
 * Regions are the main regions in the game. They are referred to as "scenes" in the game itself. To avoid confusion with 
 * Phaser scene's, they are called Regions in code.
 */
export const REGIONS = {
    BEACH: "Beach",
    DOWNTOWN: "Downtown",
    CAMP: "Camp",
    SPACE: "Space"
}

/**
 * The areas of each region. Access a region's areas via AREAS[REGIONS.[region enum value]]. For example, AREAS[REGIONS.BEACH] would return the beach areas.
 */
export const AREAS = {
    [REGIONS.BEACH]: {
        LIFEGUARD_STAND: "Lifeguard Stand",
        BEACHBALL: "Beachball",
        SEAGULL: "Seagull",
        SANDCASTLE: "Sandcastle"
    },
    [REGIONS.DOWNTOWN]: {
        BLUE_BUILDING: "Blue Building",
        TREE_AND_BENCH: "Tree & Bench",
        PEACH_BUILDING: "Peach Building",
        GREEN_BUILDING: "Green Building"
    },
    [REGIONS.CAMP]: {
        CHAIR: "CHAIR",
        TENT: "Tent",
        TREES: "Trees",
        TRUCK: "TRUCK"
    },
    [REGIONS.SPACE]: {
        GREENHOUSE: "Greenhouse",
        DOME: "Dome",
        ROVER: "Rover",
    }
}

/**
 * The Shapes in the game. These values can also be used when needing to display the shape name.
 */
export const SHAPES = {
    RECTANGLE: "Rectangle",
    PYRAMID: "Pyramid",
    RECTANGULAR_PRISM: "Rectangular Prism",
    CIRCLE: "Circle",
    SPHERE: "Sphere",
    CYLINDER: "Cylinder",
    CONE: "Cone",
    TRIANGLE: "Triangle",
    RECTANGLE: "Rectangle",
    HEXAGON: "Hexagon",
    SQUARE: "Square",
    TRIANGULAR_PRISM: "Triangular Prism",
    PENTAGON: "Pentagon",
    OVAL: "Oval",
    OCTAGON: "Octagon",
    CRESCENT: "Crescent",
    HEART: "Heart",
    CUBE: "Cube"
}

export const GameData = {
    /**
     * Add your scene classes here. Simply put the scene class.
     * Ex: `scenes: [MyScene,MyOtherScene]`
     * @type {{KEY:string}[]}
     */
    scenes: [Game],

    /**
     * Set the first scene that should be loaded here. Must match one of the scenes' key found in the scenes array.
     * It is recommended you put `MyScene.KEY` here instead of using a string.
     * @type {string}
     */
    startScene: Game.KEY,

    /**
     * Place the name of the spine file(s) here, excluding the extension.
     */
    spines: [],

    /**
     * Text that can be found in the game.
     */
    text:
    {
        flatShapes: "FLAT SHAPES",
        flatShapesOn3dPlane: "FLAT SHAPES ON 3-DIMENSIONAL PLANES",
        threeDimensionalShapes: "3-DIMENSIONAL SHAPES",
        areasExplored: "Areas\nExplored",
        shapesFound: "Shapes\nFound",
        mainInstructions: "Click on the different parts of the\nscene to explore the area!",
        areaInstructions0: "Select the shape you want to\nlook for from the list.",
        areaInstructions1: "Locate that shape in the\nimage on the right.",
    },

    region_unlock_requirements: {
        [REGIONS.BEACH]: 0,
        [REGIONS.DOWNTOWN]: 15,
        [REGIONS.CAMP]: 32,
        [REGIONS.SPACE]: 56
    },

    /**
     * The region data. This stores how many shapes are needed to be found within each area of each region.
     */
    region_data:
    {
        [REGIONS.BEACH]:
        {
            [AREAS[REGIONS.BEACH].LIFEGUARD_STAND]:
            {
                [SHAPES.RECTANGLE]: 4,
                [SHAPES.PYRAMID]: 1,
                [SHAPES.RECTANGULAR_PRISM]: 3,
            },
            [AREAS[REGIONS.BEACH].SANDCASTLE]:
            {
                [SHAPES.CYLINDER]: 2,
                [SHAPES.CONE]: 2,
            },
            [AREAS[REGIONS.BEACH].BEACHBALL]:
            {
                [SHAPES.CIRCLE]: 1,
                [SHAPES.SPHERE]: 1,
            },
            [AREAS[REGIONS.BEACH].SEAGULL]:
            {
                [SHAPES.TRIANGLE]: 2,
                [SHAPES.CONE]: 1,
                [SHAPES.SPHERE]: 1,
            },
        },
        [REGIONS.DOWNTOWN]:
        {
            [AREAS[REGIONS.DOWNTOWN].BLUE_BUILDING]:
            {
                [SHAPES.HEXAGON]: 1,
                [SHAPES.SQUARE]: 2,
            },
            [AREAS[REGIONS.DOWNTOWN].TREE_AND_BENCH]:
            {
                [SHAPES.RECTANGLE]: 2,
                [SHAPES.SQUARE]: 1,
                [SHAPES.CYLINDER]: 1,
                [SHAPES.SPHERE]: 1,
            },
            [AREAS[REGIONS.DOWNTOWN].PEACH_BUILDING]:
            {
                [SHAPES.CIRCLE]: 2,
                [SHAPES.SQUARE]: 5,
            },
            [AREAS[REGIONS.DOWNTOWN].GREEN_BUILDING]:
            {
                [SHAPES.RECTANGLE]: 5,
                [SHAPES.SQUARE]: 1,
            },
        },
        [REGIONS.CAMP]:
        {
            [AREAS[REGIONS.CAMP].CHAIR]:
            {
                [SHAPES.RECTANGLE]: 2,
                [SHAPES.SQUARE]: 2,
            },
            [AREAS[REGIONS.CAMP].TENT]:
            {
                [SHAPES.SQUARE]: 1,
                [SHAPES.TRIANGLE]: 1,
                [SHAPES.TRIANGULAR_PRISM]: 1,
            },
            [AREAS[REGIONS.CAMP].TREES]:
            {
                [SHAPES.CONE]: 1,
                [SHAPES.CYLINDER]: 1,
                [SHAPES.SPHERE]: 2,
            },
            [AREAS[REGIONS.CAMP].TRUCK]:
            {
                [SHAPES.RECTANGLE]: 4,
                [SHAPES.CIRCLE]: 2,
            },
        },
        [REGIONS.SPACE]:
        {
            [AREAS[REGIONS.SPACE].GREENHOUSE]:
            {
                [SHAPES.SQUARE]: 3,
                [SHAPES.TRIANGLE]: 1,
                [SHAPES.CYLINDER]: 9,
                [SHAPES.TRIANGULAR_PRISM]: 1,
            },
            [AREAS[REGIONS.SPACE].DOME]:
            {
                [SHAPES.RECTANGLE]: 2,
                [SHAPES.HEXAGON]: 3,
                [SHAPES.PENTAGON]: 2,
            },
            [AREAS[REGIONS.SPACE].ROVER]:
            {
                [SHAPES.RECTANGLE]: 2,
                [SHAPES.SQUARE]: 2,
                [SHAPES.CIRCLE]: 3,
                [SHAPES.CYLINDER]: 3,
            },
        },
    },

    enableSound: true,

    /**
     * The key to use when retrieving and saving game data to local storage.
     */
    savedDataKey: "GetInShapeGameData",

    /**
     * The saved game data. Stores which shapes were found in each area of each region.
     * Also stores what game version the data was saved with. 
     */
    saved_data:
    {
        gameVersion: gameInfo.gameVersion,

        [REGIONS.BEACH]:
        {
            [AREAS[REGIONS.BEACH].LIFEGUARD_STAND]:
            {
                found: false,
                shapes: {
                    [SHAPES.RECTANGLE]: [false, false, false, false, false, false],
                    [SHAPES.PYRAMID]: [false],
                    [SHAPES.RECTANGULAR_PRISM]: [false, false, false],
                }
            },
            [AREAS[REGIONS.BEACH].SEAGULL]:
            {
                found: false,
                shapes: {
                    [SHAPES.TRIANGLE]: [false, false],
                    [SHAPES.CONE]: [false],
                    [SHAPES.SPHERE]: [false],
                }
            },
            [AREAS[REGIONS.BEACH].BEACHBALL]:
            {
                found: false,
                shapes: {
                    [SHAPES.CIRCLE]: [false],
                    [SHAPES.SPHERE]: [false],
                }
            },
            [AREAS[REGIONS.BEACH].SANDCASTLE]:
            {
                found: false,
                shapes: {
                    [SHAPES.CYLINDER]: [false, false],
                    [SHAPES.CONE]: [false, false],
                }
            },
        },
        [REGIONS.DOWNTOWN]:
        {
            [AREAS[REGIONS.DOWNTOWN].BLUE_BUILDING]:
            {
                found: false,
                shapes: {
                    [SHAPES.HEXAGON]: [false],
                    [SHAPES.SQUARE]: [false, false],
                }
            },
            [AREAS[REGIONS.DOWNTOWN].TREE_AND_BENCH]:
            {
                found: false,
                shapes: {
                    [SHAPES.RECTANGLE]: [false, false],
                    [SHAPES.SQUARE]: [false],
                    [SHAPES.CYLINDER]: [false],
                    [SHAPES.SPHERE]: [false],
                }
            },
            [AREAS[REGIONS.DOWNTOWN].PEACH_BUILDING]:
            {
                found: false,
                shapes: {
                    [SHAPES.CIRCLE]: [false, false],
                    [SHAPES.SQUARE]: [false, false, false, false, false, false],
                }
            },
            [AREAS[REGIONS.DOWNTOWN].GREEN_BUILDING]:
            {
                found: false,
                shapes: {
                    [SHAPES.RECTANGLE]: [false, false, false, false, false, false, false],
                    [SHAPES.SQUARE]: [false, false],
                }
            },
        },
        [REGIONS.CAMP]:
        {
            [AREAS[REGIONS.CAMP].CHAIR]:
            {
                found: false,
                shapes: {
                    [SHAPES.RECTANGLE]: [false, false],
                    [SHAPES.SQUARE]: [false, false],
                }
            },
            [AREAS[REGIONS.CAMP].TENT]:
            {
                found: false,
                shapes: {
                    [SHAPES.SQUARE]: [false],
                    [SHAPES.TRIANGLE]: [false],
                    [SHAPES.TRIANGULAR_PRISM]: [false],
                }
            },
            [AREAS[REGIONS.CAMP].TREES]:
            {
                found: false,
                shapes: {
                    [SHAPES.CONE]: [false],
                    [SHAPES.CYLINDER]: [false, false],
                    [SHAPES.SPHERE]: [false, false],
                }
            },
            [AREAS[REGIONS.CAMP].TRUCK]:
            {
                found: false,
                shapes: {
                    [SHAPES.RECTANGLE]: [false, false, false, false, false, false, false],
                    [SHAPES.CIRCLE]: [false, false, false, false],
                }
            },
        },
        [REGIONS.SPACE]:
        {
            [AREAS[REGIONS.SPACE].GREENHOUSE]:
            {
                found: false,
                shapes: {
                    [SHAPES.SQUARE]: [false, false, false],
                    [SHAPES.TRIANGLE]: [false],
                    [SHAPES.CYLINDER]: [false, false, false, false, false, false, false, false, false],
                    [SHAPES.TRIANGULAR_PRISM]: [false],
                }
            },
            [AREAS[REGIONS.SPACE].DOME]:
            {
                found: false,
                shapes: {
                    [SHAPES.RECTANGLE]: [false, false],
                    [SHAPES.HEXAGON]: [false, false, false],
                    [SHAPES.PENTAGON]: [false, false],
                }
            },
            [AREAS[REGIONS.SPACE].ROVER]:
            {
                found: false,
                shapes: {
                    [SHAPES.RECTANGLE]: [false, false],
                    [SHAPES.SQUARE]: [false, false],
                    [SHAPES.CIRCLE]: [false, false, false],
                    [SHAPES.CYLINDER]: [false, false, false],
                }
            },
        },
    },
}

/**
 * Gets the total required shapes needed in a region.
 * @param {string} region the REGIONS enum value
 * @returns the total required shapes needed to be found in the region
 */
export function getTotalRequiredShapes(region)
{
    let totalShapes = 0;
    for (let area in GameData.region_data[region])
    {
        totalShapes += getTotalRequiredShapesForArea(region, area);
    }
    return totalShapes;
}

/**
 * Gets the total required shapes needed in an area of a region.
 * @param {string} region the REGIONS enum value
 * @param {string} area the AREAS enum value
 * @returns the total required shapes needed to be found in the area
 */
export function getTotalRequiredShapesForArea(region, area)
{
    let totalShapes = 0;
    for (let shape in GameData.region_data[region][area])
    {
        totalShapes += GameData.region_data[region][area][shape];
    }
    return totalShapes;
}

/**
 * Gets the number of areas in a region.
 * @param {string} region the REGIONS enum value
 * @returns the number of areas in the region
 */
export function getAreasInRegion(region)
{
    return Object.keys(GameData.region_data[region]).length;
}

/**
 * Gets the number of found areas in a region.
 * @param {string} region the REGIONS enum value
 * @returns the number of found areas in the region
 */
export function getFoundAreasInRegion(region)
{
    let totalFoundAreas = 0;
    for (let area in GameData.saved_data[region])
    {
        if (GameData.saved_data[region][area].found)
        {
            totalFoundAreas++;
        }
    }
    return totalFoundAreas;
}

/**
 * Gets the number of found shapes in a region (sum of all shapes found in all areas for the region).
 * @param {string} region the REGIONS enum value
 * @returns the number of found shapes in the region
 */
export function getFoundShapesInRegion(region)
{
    let totalFoundShapesInRegion = 0;
    for (let area in GameData.saved_data[region])
    {
        totalFoundShapesInRegion += getFoundShapesInArea(region, area);
    }
    return totalFoundShapesInRegion;
}

/**
 * Gets the number of found shapes within an area.
 * @param {string} region the REGIONS enum value
 * @param {string} area the AREAS enum value
 * @param {Boolean} cap if true, the value returned will be capped to the max number of shapes required for the area
 * @returns the number of found shapes within the area
 */
export function getFoundShapesInArea(region, area, cap = true)
{
    let totalFoundShapesInArea = 0;
    for (let shape in GameData.saved_data[region][area].shapes)
    {
        totalFoundShapesInArea += getFoundShapesForAreaShapes(region, area, shape, cap);
    }
    return totalFoundShapesInArea;
}

/**
 * Gets the number of found shapes for a specific shape within an area.
 * @param {string} region the REGIONS enum value
 * @param {string} area the AREAS enum value
 * @param {string} shape the SHAPES enum value
 * @param {Boolean} cap if true, the value returned will be capped to the max number of shapes required for that shape within the area
 * @returns the number of found shapes for the shape within the area
 */
export function getFoundShapesForAreaShapes(region, area, shape, cap = true)
{
    let totalFoundShapesForAreaShapes = 0;
    GameData.saved_data[region][area].shapes[shape].forEach((b) => { if (b) totalFoundShapesForAreaShapes++ });
    if (cap && totalFoundShapesForAreaShapes > GameData.region_data[region][area][shape])
    {
        totalFoundShapesForAreaShapes = GameData.region_data[region][area][shape];
    }

    return totalFoundShapesForAreaShapes;
}

/**
 * Gets the total number of areas found within all regions.
 * @returns the number of found areas across all regions.
 */
export function getTotalFoundAreas()
{
    return getFoundAreasInRegion(REGIONS.BEACH) +
        getFoundAreasInRegion(REGIONS.DOWNTOWN) +
        getFoundAreasInRegion(REGIONS.CAMP) +
        getFoundAreasInRegion(REGIONS.SPACE);

}

/**
 * Gets the total number of shapes found within all regions.
 * @returns the number of found shapes across all regions.
 */
export function getTotalFoundShapes()
{
    return getFoundShapesInRegion(REGIONS.BEACH) +
        getFoundShapesInRegion(REGIONS.DOWNTOWN) +
        getFoundShapesInRegion(REGIONS.CAMP) +
        getFoundShapesInRegion(REGIONS.SPACE);
}

/**
 * Saves the game data to local storage.
 */
export function saveGameData()
{
    ABCyaLib.Storage.saveObject(GameData.savedDataKey, GameData.saved_data);
}

/**
 * Loads the game data from local storage.
 */
export function loadGameData()
{
    let data = ABCyaLib.Storage.getObject(GameData.savedDataKey);
    if (data != null)
    {
        GameData.saved_data = data;
    }
    ABCyaLib.Storage.saveObject(GameData.savedDataKey, GameData.saved_data);
}

/**
 * Resets a region data.
 * @param {string} region the REGIONS enum value
 */
function resetRegionData(region)
{
    for (let area in GameData.saved_data[region])
    {
        resetAreaData(region, area);
    }
}

/**
 * Resets an area data for a region.
 * @param {string} region the REGIONS enum value
 * @param {string} area the AREA enum value
 */
function resetAreaData(region, area)
{
    GameData.saved_data[region][area].found = false;
    for (let shape in GameData.saved_data[region][area].shapes)
    {
        resetShapeData(region, area, shape);
    }
}

/**
 * Resets a shape data for an area within a region.
 * @param {string} region the REGIONS enum value
 * @param {string} area the AREAS enum value
 * @param {string} shape the SHAPES enum value
 */
function resetShapeData(region, area, shape)
{
    for (let i = 0; i < GameData.saved_data[region][area].shapes[shape].length; i++)
    {
        GameData.saved_data[region][area].shapes[shape][i] = false;
    }
}

/**
 * Resets the game data. This is used when the new game option is selected.
 */
export function resetGameData()
{
    resetRegionData(REGIONS.BEACH);
    resetRegionData(REGIONS.DOWNTOWN);
    resetRegionData(REGIONS.CAMP);
    resetRegionData(REGIONS.SPACE);
    saveGameData();
}

export default GameData;